import React from 'react';
import { TextField, TextFieldProps } from '@mui/material';
import { deepmerge } from '@mui/utils';
import { PounceStyleProps, extractPounceProps, mapPouncePropsToSx } from '../../utils';

export interface TextAreaProps
  extends Omit<TextFieldProps, 'multiline' | keyof PounceStyleProps>, PounceStyleProps {
  /** The label that is associated with this textarea */
  label: string;

  /** Whether the textarea has an invalid value */
  invalid?: boolean;

  /** Whether the textarea is required or not */
  required?: boolean;

  /** Whether the textarea is disabled or not */
  disabled?: boolean;

  /** Whether the textarea is read-only (scrollable but not editable) */
  readOnly?: boolean;

  /** Additional styles to apply (combined with pounce props) */
  sx?: TextFieldProps['sx'];
}

/**
 * TextArea component built on top of MUI TextField with multiline support.
 *
 * Under the hood it renders a `textarea` element, so you
 * can pass it as many native attrs as you wish.
 *
 * Provides all the functionality from the original Pounce TextArea while leveraging
 * MUI's accessibility and interaction patterns.
 */
export const TextArea = React.forwardRef<HTMLDivElement, TextAreaProps>(function TextArea(
  { label, invalid, required, disabled, readOnly, hidden, id, name, value, sx, ...rest },
  ref,
) {
  const { pounceProps, otherProps } = extractPounceProps(rest);
  const pounceStyles = mapPouncePropsToSx(pounceProps);

  // Combine pounce-generated styles with user sx and textarea padding using MUI's deepmerge utility
  const combinedSx = deepmerge(pounceStyles, sx || {}) as TextFieldProps['sx'];

  return (
    <TextField
      ref={ref}
      id={id}
      name={name}
      label={label}
      value={value}
      multiline
      fullWidth
      required={required}
      disabled={disabled}
      error={invalid}
      sx={combinedSx}
      hidden={hidden}
      slotProps={{
        input: {
          'aria-invalid': invalid,
          readOnly,
        },
        htmlInput: {
          'aria-required': required,
        },
      }}
      {...otherProps}
    />
  );
});
